<template>
  <UiAutopaymentModal
    v-if="PaymentModal.isShow && autopaymentModalData && landing?.paymentData"
    :data="autopaymentModalData"
    :logged-data="getUserDataForButton"
    :form-model-data="formFillData"
    :price-banner="subscriptionData?.priceBanner"
    @click:pay="handleClickPayPopup"
    @click:close="handleAutopaymentModalClose"
    @click:login="VKConnectStore.login"
  />

  <UiThanksModal
    v-if="PaymentModal.isThanksPopupOpen"
    :title="PaymentModal.finishModalData.title"
    :description="PaymentModal.finishModalData.description"
    :button-text="PaymentModal.finishModalData.buttonText"
    :button-theme="PaymentModal.finishModalData.buttonTheme"
    @click:close="handleThanksModalClick"
  />
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import UiAutopaymentModal from '@academica-box/components/Modals/UiAutopaymentModal/UiAutopaymentModal.vue'
  import UiThanksModal from '@academica-box/components/Modals/UiThanksModal/UiThanksModal.vue'
  import type { ButtonThemes, PaymentFormData, CloudpaymentsResult } from '@academica-box/types'
  import { useUserStore } from '@academica-box/composables/store/user'
  import { useVKConnectStore } from '@academica-box/composables/store/VKConnect'
  import { useSubscribesStore } from '@academica-box/composables/store/subscribes'
  import {
    OpenCourseRequestAccessTypeEnum,
    SubscriptionOpenPostBodyRequestCountryCodeEnum,
    SubscriptionOpenPostBodyRequestCurrencyCodeEnum,
    SubscriptionOpenPostBodyRequestLanguageCodeEnum,
    SubscriptionOpenPostBodyRequestPageTypeEnum,
    SubscriptionOpenPostBodyRequestTypeEnum,
  } from '@academica-box/swagger/academgen'
  import { getLmsCourseLink } from '@academica-box/helpers/links'
  import { getCloudpaymentsData } from '@academica-box/helpers/getCloudpaymentsData'
  import { getSubscribeCloudpaymentsData } from '@academica-box/helpers/getSubscribeCloudpaymentsData'
  import { generateRequestId } from '@academica-box/helpers/requestId'
  import { getSubscriptionData } from './helpers'
  import { useAnalytics } from './analytics'
  import { useLandingStore } from '@/store/landing'
  import { SmartComponents, useSmartComponentsStore } from '@/store/smartComponents'
  import {
    sendLandingClickFormButton,
    sendLandingSuccessfulPayment,
    sendLandingSuccessfulSubmit,
    sendLandingSuccessfulSubscribe,
    sendLandingUnsuccessfulPayment,
    sendLandingUnsuccessfulSubmit
  } from '@/analytics/events'

  const config = useRuntimeConfig()

  const analytics = useAnalytics()

  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const VKConnectStore = useVKConnectStore()
  const { getUserDataForButton, getUserDataForForm } = storeToRefs(VKConnectStore)
  const landingStore = useLandingStore()
  const { landing, redirectUrl, requestId } = storeToRefs(landingStore)
  const subscribesStore = useSubscribesStore()
  const { activeSubscription, hasSubcribe, userSubsription, roundedSubscriptionCoursesCount } = storeToRefs(subscribesStore)
  const smartComponentsStore = useSmartComponentsStore()
  const { PaymentModal } = storeToRefs(smartComponentsStore)

  const autopaymentModalUserData = ref<PaymentFormData | null>(null)
  const isPaymentLoading = ref(false)

  const activeAccessType = computed(() => {
    return landing?.value?.commonData.accessType as unknown as OpenCourseRequestAccessTypeEnum || OpenCourseRequestAccessTypeEnum.Paid
  })

  const subscriptionData = computed(() => {
    if (!landing?.value || !activeSubscription?.value) {
      return null
    }
    return getSubscriptionData(landing?.value, activeSubscription?.value, hasSubcribe.value, roundedSubscriptionCoursesCount.value)
  })

  const autopaymentModalData = computed(() => {
    if (PaymentModal.value.isSubcribeProgress) {
      return subscriptionData.value?.data
    }

    if (!landing?.value?.paymentData?.data) {
      return
    }

    if (activeAccessType.value === OpenCourseRequestAccessTypeEnum.Paid) {
      return {
        ...landing.value.paymentData.data,
        free: false,
      }
    } else {
      return landing.value.paymentData.data
    }
  })

  const formFillData = computed((): PaymentFormData | null => {
    if (autopaymentModalUserData?.value) {
      return {
        name: autopaymentModalUserData.value.name,
        email: autopaymentModalUserData.value.email,
        policyAccepted: 0,
        'docs[]': 'https://skillbox.ru/privacy_policy.pdf', // TODO Реализовать нормальную отправку форм в случае необходимости
      }
    }

    if (user?.value) {
      return {
        name: user.value.name,
        email: user.value.email,
        policyAccepted: 0,
        'docs[]': 'https://skillbox.ru/privacy_policy.pdf', // TODO Реализовать нормальную отправку форм в случае необходимости
      }
    }

    if (getUserDataForForm?.value) {
      return {
        name: getUserDataForForm.value.name,
        email: getUserDataForForm.value.email,
        policyAccepted: 0,
        'docs[]': 'https://skillbox.ru/privacy_policy.pdf', // TODO Реализовать нормальную отправку форм в случае необходимости
      }
    }

    return {
      name: '',
      email: '',
      policyAccepted: 0,
      'docs[]': 'https://skillbox.ru/privacy_policy.pdf', // TODO Реализовать нормальную отправку форм в случае необходимости
    }
  })

  const openThanksPopup = (finishModalData: {
    title: string;
    description: string;
    buttonText: string;
    buttonTheme?: ButtonThemes;
  }) => {
    smartComponentsStore.set(SmartComponents.PaymentModal, {
      isShow: false,
      isSubcribeProgress: false,
      isThanksPopupOpen: true,
      finishModalData,
    })
  }

  const closeThanksPopup = () => {
    const buttonText = smartComponentsStore[SmartComponents.PaymentModal].finishModalData.buttonText

    if (buttonText === 'Начать учиться') {
      redirectUrl.value = getLmsCourseLink(landing?.value?.commonData.course_type, landing?.value?.commonData.slug)
    }

    if (buttonText === 'Выбрать курсы' || buttonText === 'Перейти к курсам') {
      redirectUrl.value = `${config.public.frontUrl}/catalog/?is_subscribe_available=1`
    }
    smartComponentsStore.set(SmartComponents.PaymentModal, {
      isShow: false,
      isSubcribeProgress: false,
      isThanksPopupOpen: false,
      finishModalData: undefined,
    })
  }

  const openCourse = async (model: PaymentFormData, accessType: OpenCourseRequestAccessTypeEnum) => {
    if (!landing?.value) {
      return
    }

    const { $api } = useNuxtApp()

    const recaptchaToken = await $api.recaptcha.generateToken()

    return await landingStore.openCourse({
      name: model.name,
      email: model.email,
      policy_accepted: model.policyAccepted,
      request_id: generateRequestId('AG'),
      access_type: accessType,
      nomenclature_id: landing.value.commonData.nomenclature_id,
      recaptcha_token: recaptchaToken || '',
      'docs[]': 'https://skillbox.ru/privacy_policy.pdf', // TODO Реализовать нормальную отправку документов в случае необходимости
    })
  }

  const sendGatewaySubscribeData = async (model: PaymentFormData) => {
    const { $api } = useNuxtApp()

    if (!landing?.value || !activeSubscription?.value) {
      return
    }

    const recaptchaToken = await $api.recaptcha.generateToken()

    return await landingStore.openSubscription({
      name: model.name,
      email: model.email,
      country_code: SubscriptionOpenPostBodyRequestCountryCodeEnum.Ru,
      language_code: SubscriptionOpenPostBodyRequestLanguageCodeEnum.Ru,
      currency_code: SubscriptionOpenPostBodyRequestCurrencyCodeEnum.Rub,
      request_id: generateRequestId('AG'),
      request_url: location.href,
      subscription_plan_id: activeSubscription?.value?.id,
      page_type: SubscriptionOpenPostBodyRequestPageTypeEnum.SubscribePro,
      type: SubscriptionOpenPostBodyRequestTypeEnum.SubscribePro,
      recaptcha_token: recaptchaToken || '',
    })
  }

  const createCloudpaymentsWidget = (model: PaymentFormData) => {
    if (!landing?.value || !requestId?.value) {
      return
    }

    const cloudPaymentsData = getCloudpaymentsData(
      landing.value,
      model,
      requestId.value,
    )

    const widget = new (window as any).cp.CloudPayments()

    widget.pay('charge', cloudPaymentsData, {
      onComplete: (result: CloudpaymentsResult) => {
        if (!result.success) {
          sendLandingUnsuccessfulPayment({
            reason: result.message,
            request_id: requestId?.value,
            nomenclature_id: landing?.value?.commonData.nomenclature_id,
            nomenclature_type: activeAccessType.value,
            user_id: Number(user?.value?.id),
          })
        } else {
          sendLandingSuccessfulPayment({
            nomenclature_id: landing?.value?.commonData.nomenclature_id,
            nomenclature_type: activeAccessType.value,
            user_id: Number(user?.value?.id),
            request_id: requestId?.value,
          })
        }
      },
      onSuccess: () => {
        analytics.tryEdpartnersPayment(model)

        openThanksPopup({
          title: 'Готово!',
          description: 'Проверьте почту — отправили <br> письмо с доступом к курсу',
          buttonText: 'Хорошо',
        })
      },
      onFail: (reason: string) => {
        sendLandingUnsuccessfulPayment({
          reason: reason.replaceAll(' ', '_'),
          request_id: requestId?.value,
          nomenclature_id: landing?.value?.commonData.nomenclature_id,
          nomenclature_type: activeAccessType.value,
          user_id: Number(user?.value?.id),
        })
      }
    })
  }

  const createSubscribeCloudpaymentsWidget = (model: PaymentFormData) => {
    if (!landing?.value || !requestId?.value) {
      return
    }

    const cloudPaymentsData = getSubscribeCloudpaymentsData(
      landing.value,
      model,
      requestId.value,
      activeSubscription?.value,
      userSubsription?.value
    )

    const widget = new (window as any).cp.CloudPayments()

    widget.pay('charge', cloudPaymentsData, {
      onComplete: (result: CloudpaymentsResult) => {
        if (!result.success) {
          sendLandingUnsuccessfulPayment({
            reason: result.message,
            request_id: requestId?.value,
            nomenclature_id: landing?.value?.commonData.nomenclature_id,
            nomenclature_type: activeAccessType.value,
            user_id: Number(user?.value?.id),
          })
        } else {
          sendLandingSuccessfulPayment({
            nomenclature_id: landing?.value?.commonData.nomenclature_id,
            nomenclature_type: activeAccessType.value,
            user_id: Number(user?.value?.id),
            request_id: requestId?.value,
          })
        }
      },
      onSuccess: () => {
        subscribesStore.openCourse({
          nomenclature_id: landing?.value?.commonData.nomenclature_id,
          subscription_plan_id: activeSubscription?.value?.id,
        }).then(() => {
          openThanksPopup({
            title: 'Ура, курс открыт',
            description: 'Открыли доступ к&nbsp;выбранному курсу. Больше интересного по&nbsp;подписке&nbsp;&mdash; в&nbsp;нашем каталоге. Ищите курсы с&nbsp;пометкой Pro',
            buttonText: 'Начать учиться',
            buttonTheme: 'senary'
          })
        })
      },
      onFail: (reason: string) => {
        sendLandingUnsuccessfulPayment({
          reason: reason.replaceAll(' ', '_'),
          request_id: requestId?.value,
          nomenclature_id: landing?.value?.commonData.nomenclature_id,
          nomenclature_type: activeAccessType.value,
          user_id: Number(user?.value?.id),
        })
      }
    })
  }

  const handleClickPayPopup = (model: PaymentFormData) => {
    if (PaymentModal.value.isSubcribeProgress) {
      subscribeUser(model)
    } else {
      buyCourse(model)
    }
  }

  const subscribeUser = (model: PaymentFormData) => {
    if (!landing?.value || isPaymentLoading.value) {
      return
    }

    sendLandingClickFormButton({
      nomenclature_id: landing?.value?.commonData.nomenclature_id,
      nomenclature_type: activeAccessType.value,
      user_id: Number(user?.value?.id),
    })

    isPaymentLoading.value = true

    sendGatewaySubscribeData(model).then(() => {
      smartComponentsStore.set(SmartComponents.PaymentModal, { isShow: false })

      createSubscribeCloudpaymentsWidget(model)

      sendLandingSuccessfulSubscribe({
        request_id: requestId?.value,
        nomenclature_id: landing?.value?.commonData.nomenclature_id,
        nomenclature_type: activeAccessType.value,
        user_id: Number(user?.value?.id),
      })
    }).catch(() => {
      smartComponentsStore.set(SmartComponents.PaymentModal, {
        isThanksPopupOpen: true,
        isShow: false,
        isSubcribeProgress: false,
        finishModalData: {
          title: 'Произошла ошибка',
          description: 'Что-то пошло не так',
          buttonText: 'Понятно'
        }
      })

      sendLandingUnsuccessfulSubmit({
        reason: 'Backend_unsuccessful',
        request_id: requestId?.value,
        nomenclature_id: landing?.value?.commonData.nomenclature_id,
        nomenclature_type: activeAccessType.value,
        user_id: Number(user?.value?.id),
        name: model.name,
        email: model.email,
      })
    }).finally(() => {
      isPaymentLoading.value = false
    })
  }

  const buyCourse = (model: PaymentFormData) => {
    if (!landing?.value || isPaymentLoading.value) {
      return
    }

    sendLandingClickFormButton({
      nomenclature_id: landing?.value?.commonData.nomenclature_id,
      nomenclature_type: activeAccessType.value,
      user_id: Number(user?.value?.id),
    })

    isPaymentLoading.value = true

    openCourse(model, activeAccessType.value).then(() => {
      smartComponentsStore.set(SmartComponents.PaymentModal, { isShow: false })

      sendLandingSuccessfulSubmit({
        request_id: requestId?.value,
        nomenclature_id: landing?.value?.commonData.nomenclature_id,
        nomenclature_type: activeAccessType.value,
        user_id: Number(user?.value?.id),
      })

      analytics.moreSlov()

      if (activeAccessType.value === OpenCourseRequestAccessTypeEnum.Paid) {
        analytics.tryEdpartnersOrder(model, 2)
        createCloudpaymentsWidget(model)
      } else {
        analytics.tryEdpartnersOrder(model, 3)

        openThanksPopup({
          title: 'Готово!',
          description: 'Открыли доступ <br> к материалам курса',
          buttonText: 'Начать учиться',
        })
      }

      isPaymentLoading.value = false
    }).catch(() => {
      sendLandingUnsuccessfulSubmit({
        reason: 'Backend_unsuccessful',
        request_id: requestId?.value,
        nomenclature_id: landing?.value?.commonData.nomenclature_id,
        nomenclature_type: activeAccessType.value,
        user_id: Number(user?.value?.id),
      })
    })
  }

  const handleAutopaymentModalClose = (model: PaymentFormData) => {
    sendLandingUnsuccessfulSubmit({
      reason: 'User_has_closed_popup',
      request_id: requestId?.value,
      nomenclature_id: landing?.value?.commonData.nomenclature_id,
      nomenclature_type: activeAccessType.value,
      user_id: Number(user?.value?.id),
      name: model.name,
      email: model.email,
    })

    const { name, email } = model

    if (name || email) {
      autopaymentModalUserData.value = model
    }

    smartComponentsStore.set(SmartComponents.PaymentModal, { isShow: false })
  }

  const handleThanksModalClick = () => {
    closeThanksPopup()

    if (redirectUrl.value) {
      location.href = redirectUrl.value
    }
  }
</script>
